import { ResponsivePreview } from "@/components/illustrations/ResponsivePreview";
import { SitemapPreview } from "@/components/illustrations/SitemapPreview";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import {
  HowItWorks,
  StatusRow,
  MetricCard,
  CheckItem,
  FakeSelect,
  ProgressBar,
  Tag,
} from "@/components/HowItWorks";
import { Button } from "@/components/ui/button";
import {
  Accordion,
  AccordionItem,
  AccordionTrigger,
  AccordionContent,
} from "@/components/ui/accordion";
import {
  Globe,
  ArrowRight,
  ArrowUpRight,
  Layout,
  Languages,
  Code,
  RefreshCw,
  FileText,
  Search,
  Users,
  Accessibility,
  CalendarDays,
  Monitor,
  Gauge,
  Smartphone,
  Tablet,
} from "lucide-react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { useLanguage } from "@/contexts/LanguageContext";
import { getTranslation } from "@/lib/translations";

import webflowLogo from "@/assets/partners/webflow-icon.svg";
import nextjsLogo from "@/assets/partners/nextjs.svg";
import sanityLogo from "@/assets/partners/sanity.svg";
import weglotLogo from "@/assets/partners/weglot.svg";
import { usePageSEO, buildHreflang } from "@/hooks/usePageSEO";
import { useCmsPageSEO } from "@/hooks/useCmsPageSEO";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { resolveAssetUrl } from "@/lib/media";

const highlights = [
  {
    icon: Globe,
    title: "Webflow Development",
    description:
      "Pixel-perfect, CMS-driven Webflow sites giving your team full control without developer dependency.",
  },
  {
    icon: Layout,
    title: "Corporate Websites",
    description:
      "Professional, brand-aligned websites for enterprises that communicate credibility and drive engagement.",
  },
  {
    icon: Code,
    title: "Headless CMS Solutions",
    description:
      "Decouple your frontend from your content layer for maximum flexibility, speed, and performance.",
  },
];

const processSteps = [
  {
    title: "Research & Planning",
    description:
      "We analyse your audience, competitors, and goals to define the right sitemap, content strategy, and technology stack.",
    preview: <SitemapPreview />,
  },
  {
    title: "Design & Prototype",
    description:
      "We design responsive, brand-aligned layouts in Figma and build interactive prototypes for review before development.",
    preview: <ResponsivePreview />,
  },
  {
    title: "Development & Testing",
    description:
      "We develop your site with clean code, CMS integration, and thorough cross-browser and device testing.",
    preview: (
      <div className="space-y-2">
        <StatusRow label="Chrome" status="Passed" connected icon={Globe} delay={0.05} />
        <StatusRow label="Safari" status="Passed" connected icon={Globe} delay={0.1} />
        <StatusRow label="Firefox" status="Passed" connected icon={Globe} delay={0.15} />
        <StatusRow label="Edge" status="Testing" icon={Globe} delay={0.2} />
        <ProgressBar label="Test Coverage" percent={92} color="bg-emerald-500" delay={0.25} icon={Code} />
      </div>
    ),
  },
  {
    title: "Launch & Optimise",
    description:
      "We launch with SEO, analytics, and performance optimisation baked in, then iterate based on real data.",
    preview: (
      <div className="space-y-3">
        <div className="grid grid-cols-3 gap-2">
          <MetricCard label="Performance" value="96" trend="up" icon={Gauge} delay={0.05} />
          <MetricCard label="SEO" value="92" trend="up" icon={Search} delay={0.1} />
          <MetricCard label="A11y" value="100" icon={Accessibility} delay={0.15} />
        </div>
        <div className="bg-card rounded-xl p-4 shadow-[0_1px_2px_rgba(10,13,20,0.03),0_0_0_1px_rgba(23,23,23,0.04)]">
          <p className="text-xs font-medium text-muted-foreground mb-2.5 flex items-center gap-1.5"><Gauge className="h-3 w-3" />Core Web Vitals</p>
          <div className="space-y-2.5">
            <ProgressBar label="LCP" percent={95} color="bg-emerald-500" delay={0.2} />
            <ProgressBar label="FID" percent={98} color="bg-emerald-500" delay={0.25} />
            <ProgressBar label="CLS" percent={100} color="bg-emerald-500" delay={0.3} />
          </div>
        </div>
      </div>
    ),
  },
];

const services = [
  {
    icon: Globe,
    title: "Webflow Website Development",
    description:
      "Pixel-perfect, CMS-driven Webflow sites giving your team full control without developer dependency.",
    slug: "webflow-website-development",
  },
  {
    icon: Layout,
    title: "Corporate Websites",
    description:
      "Professional, brand-aligned websites for enterprises that communicate credibility and drive engagement.",
    slug: "corporate-websites",
  },
  {
    icon: FileText,
    title: "Landing Pages",
    description:
      "High-converting landing pages for campaigns, product launches, and lead generation, fast to iterate.",
    slug: "landing-pages",
  },
  {
    icon: Languages,
    title: "Multilingual Websites",
    description:
      "Reach global audiences with fully localised, multi-language sites built for international SEO.",
    slug: "multilingual-websites",
  },
  {
    icon: Code,
    title: "Headless CMS Solutions",
    description:
      "Decouple your frontend from your content layer for maximum flexibility, speed, and performance.",
    slug: "headless-cms-solutions",
  },
  {
    icon: RefreshCw,
    title: "Website Redesign & Optimisation",
    description:
      "Modernise outdated sites with improved UX, faster load times, and a refreshed brand experience.",
    slug: "website-redesign-optimisation",
  },
];

const techStack = [
  {
    name: "Webflow",
    logo: webflowLogo,
    description:
      "Visual web development platform for building responsive, CMS-driven websites without code.",
  },
  {
    name: "Next.js",
    logo: nextjsLogo,
    description:
      "React framework for building fast, SEO-friendly web applications with server-side rendering.",
  },
  {
    name: "Sanity",
    logo: sanityLogo,
    description:
      "Flexible headless CMS for structured content management and real-time collaboration.",
  },
  {
    name: "Weglot",
    logo: weglotLogo,
    description:
      "Multilingual translation solution that integrates with any website for instant localisation.",
  },
];

const faqs = [
  {
    q: "What CMS platforms do you work with?",
    a: "We primarily build on Webflow, WordPress, and headless CMS platforms like Sanity and Contentful. We recommend the best platform based on your content needs, team skills, and scalability requirements.",
  },
  {
    q: "How long does a website project take?",
    a: "A standard website takes 4\u20136 weeks from discovery to launch. Larger corporate sites or complex headless builds can take 8\u201312 weeks depending on scope, integrations, and content volume.",
  },
  {
    q: "Do you support multilingual websites?",
    a: "Yes. We build fully localised, multi-language websites with proper hreflang tags, RTL support for Arabic, and SEO-optimised content for each language variant.",
  },
  {
    q: "What happens after launch?",
    a: "We provide post-launch support including performance monitoring, CMS training, SEO tracking, and iterative improvements based on analytics data. We also offer retainer packages for ongoing development.",
  },
  {
    q: "Can you redesign my existing website?",
    a: "Absolutely. We audit your current site for UX issues, performance bottlenecks, and SEO gaps, then redesign with a modern stack while preserving your existing content and search rankings.",
  },
];

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  visible: (i: number = 0) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.5, delay: i * 0.1, ease: [0.25, 0.1, 0.25, 1] },
  }),
};

const WebPage = () => {
  const { lang, localePath } = useLanguage();
  const t = (key: string) => getTranslation(key, lang);
  const { canonical, hreflang } = buildHreflang("/web");
  useCmsPageSEO({
    title:
      lang === "ar"
        ? "وكالة تطوير الويب في الكويت | وافاي"
        : "Web Development Agency in Kuwait | Wavai",
    description:
      lang === "ar"
        ? "تطوير ويب مخصّص في الكويت من وافاي. مواقع سريعة وآمنة وجاهزة للسيو على ووردبريس وويبفلو ومنصات حديثة لأبرز العلامات في الخليج."
        : "Custom web development in Kuwait by Wavai. Fast, secure, SEO-ready websites on WordPress, Webflow and modern stacks for ambitious GCC brands.",
    keywords:
      "web development Kuwait, Webflow Kuwait, WordPress Kuwait, corporate websites Kuwait, multilingual websites GCC",
    canonical,
    hreflang,
    jsonLd: {
      "@context": "https://schema.org",
      "@type": "Service",
      name: "Web Development Services",
      provider: {
        "@type": "Organization",
        name: "Wavai",
        url: "https://www.wavai.ae",
      },
      description:
        "Professional Webflow websites, landing pages, and corporate web development.",
    },
  });

  const { data: works } = useQuery({
    queryKey: ["web-work"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("work")
        .select(
          "title, slug, short_description, featured_image, desktop_screenshot, industry, platforms"
        )
        .eq("status", "published")
        .or(
          "platforms.cs.{Webflow},platforms.cs.{WordPress},platforms.cs.{WooCommerce}"
        )
        .order("sort_order", { ascending: true })
        .limit(4);
      if (error) throw error;
      return data;
    },
  });

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <main>
        {/* ------------------------------------------------------------------ */}
        {/* HERO                                                               */}
        {/* ------------------------------------------------------------------ */}
        <section className="pt-36 pb-16 lg:pt-48 lg:pb-24">
          <div className="container px-6">
            <div className="max-w-3xl mx-auto text-center">
              <motion.span
                className="inline-flex items-center px-3 py-1 rounded-lg bg-secondary text-sm text-muted-foreground font-medium mb-6"
                initial="hidden"
                animate="visible"
                variants={fadeUp}
                custom={0}
              >
                {t("Web Development")}
              </motion.span>

              <motion.h1
                className="text-4xl sm:text-5xl lg:text-[3.5rem] font-display font-bold leading-tight mb-6"
                initial="hidden"
                animate="visible"
                variants={fadeUp}
                custom={1}
              >
                {t("Web Development Agency in Kuwait")}
              </motion.h1>

              <motion.p
                className="text-base lg:text-lg text-muted-foreground max-w-xl mx-auto mb-10 leading-relaxed"
                initial="hidden"
                animate="visible"
                variants={fadeUp}
                custom={2}
              >
                {t(
                  "Wavai builds fast, secure, SEO-ready websites for Kuwait's most ambitious brands. From corporate sites to complex web apps, we deliver clean code, premium design and measurable business results."
                )}
              </motion.p>

              <motion.div
                className="flex flex-col sm:flex-row items-center justify-center gap-4 [&>*]:w-full [&>*]:sm:w-auto"
                initial="hidden"
                animate="visible"
                variants={fadeUp}
                custom={3}
              >
                <Button variant="hero" size="xl" asChild>
                  <Link to={localePath("/start-a-project")}>
                    {t("Start a Project")} <ArrowRight className="h-5 w-5" />
                  </Link>
                </Button>
                <Button variant="heroOutline" size="xl" asChild>
                  <Link to={localePath("/contact")}>{t("Book a Call")}</Link>
                </Button>
              </motion.div>
            </div>

            {/* 3 Highlight cards */}
            <div className="grid md:grid-cols-3 gap-6 mt-16">
              {highlights.map((h, i) => (
                <motion.div
                  key={h.title}
                  className="bg-secondary/50 rounded-2xl p-8 flex flex-col gap-8"
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true }}
                  variants={fadeUp}
                  custom={i}
                >
                  <div className="w-12 h-12 rounded-xl bg-card flex items-center justify-center shadow-card border border-border">
                    <h.icon
                      className="h-6 w-6 text-foreground"
                      strokeWidth={1.5}
                    />
                  </div>
                  <div>
                    <h3 className="font-sans font-medium text-lg mb-2">
                      {t(h.title)}
                    </h3>
                    <p className="text-base text-muted-foreground leading-relaxed text-balance">
                      {t(h.description)}
                    </p>
                  </div>
                  <span className="mt-auto inline-flex items-center gap-1.5 text-base text-muted-foreground font-medium">
                    {t("Learn more")}
                    <ArrowUpRight className="h-4 w-4" />
                  </span>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* ------------------------------------------------------------------ */}
        {/* HOW IT WORKS                                                       */}
        {/* ------------------------------------------------------------------ */}
        <HowItWorks
          steps={processSteps}
          heading={t("From concept to launch, seamlessly")}
          subtitle={t(
            "A structured approach that delivers beautiful, high-performance websites on time."
          )}
          trustText={t("Trusted by businesses across Kuwait and the GCC.")}
          t={t}
        />

        {/* ------------------------------------------------------------------ */}
        {/* SERVICES                                                           */}
        {/* ------------------------------------------------------------------ */}
        <section className="py-16 lg:py-24 bg-secondary/30">
          <div className="container px-6">
            <div className="max-w-2xl mx-auto text-center mb-12">
              <motion.span
                className="inline-flex items-center px-3 py-1 rounded-lg bg-background text-sm text-muted-foreground font-medium mb-4"
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                variants={fadeUp}
                custom={0}
              >
                {t("What We Offer")}
              </motion.span>
              <motion.h2
                className="text-3xl sm:text-4xl lg:text-[2.5rem] font-display font-bold leading-tight mb-4"
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                variants={fadeUp}
                custom={1}
              >
                {t("Web Development Services")}
              </motion.h2>
              <motion.p
                className="text-base text-muted-foreground leading-relaxed"
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                variants={fadeUp}
                custom={2}
              >
                {t(
                  "Beautifully crafted websites that perform, built with modern tools and best practices."
                )}
              </motion.p>
            </div>

            <div className="grid sm:grid-cols-2 lg:grid-cols-3 border-t border-border">
              {services.map((service, i) => (
                <motion.div
                  key={service.slug}
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true }}
                  variants={fadeUp}
                  custom={i}
                  className="border-b border-border"
                >
                  <Link
                    to={localePath(`/web/${service.slug}`)}
                    className="group block p-8 h-full rounded-none hover:rounded-2xl hover:bg-card hover:shadow-[0_8px_30px_-12px_hsl(var(--foreground)/0.12)] transition-[background-color,border-radius,box-shadow,transform] duration-200 ease-out hover:-translate-y-0.5"
                  >
                    <div className="w-11 h-11 rounded-xl bg-primary/8 flex items-center justify-center mb-6 transition-colors duration-200 group-hover:bg-primary/15">
                      <service.icon
                        className="h-5 w-5 text-primary"
                        strokeWidth={1.5}
                      />
                    </div>
                    <h3 className="font-sans font-medium text-lg mb-2 group-hover:text-primary transition-colors duration-200">
                      {t(service.title)}
                    </h3>
                    <p className="text-sm text-muted-foreground leading-relaxed">
                      {t(service.description)}
                    </p>
                  </Link>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* ------------------------------------------------------------------ */}
        {/* TECH STACK                                                         */}
        {/* ------------------------------------------------------------------ */}
        <section className="py-16 lg:py-24">
          <div className="container px-6">
            <div className="grid lg:grid-cols-2 gap-12 lg:gap-20">
              <motion.div
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                variants={fadeUp}
                custom={0}
              >
                <span className="inline-flex items-center px-3 py-1 rounded-lg bg-secondary text-sm text-muted-foreground font-medium mb-4">
                  {t("Tech Stack")}
                </span>
                <h2 className="text-3xl sm:text-4xl lg:text-[2.5rem] font-display font-bold leading-tight mb-4">
                  {t("Built with modern tools you can trust")}
                </h2>
                <p className="text-base text-muted-foreground leading-relaxed mb-6">
                  {t(
                    "We use industry-leading platforms and frameworks to deliver websites that are fast, scalable, and easy to manage."
                  )}
                </p>
                <Link
                  to={localePath("/contact")}
                  className="inline-flex items-center gap-1.5 text-base text-primary font-medium hover:underline"
                >
                  {t("Discuss your project")}
                  <ArrowUpRight className="h-4 w-4" />
                </Link>
              </motion.div>

              <div className="flex flex-col gap-6">
                {techStack.map((item, i) => (
                  <motion.div
                    key={item.name}
                    className="flex gap-5 items-start"
                    initial="hidden"
                    whileInView="visible"
                    viewport={{ once: true }}
                    variants={fadeUp}
                    custom={i}
                  >
                    <div className="w-16 h-16 rounded-2xl border border-border bg-card flex items-center justify-center shrink-0 shadow-card">
                      <img src={item.logo} alt={item.name} className="h-7 w-7 object-contain" />
                    </div>
                    <div>
                      <h3 className="font-sans font-medium text-lg mb-1">
                        {item.name}
                      </h3>
                      <p className="text-base text-muted-foreground leading-relaxed">
                        {t(item.description)}
                      </p>
                    </div>
                  </motion.div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* ------------------------------------------------------------------ */}
        {/* WORK SHOWCASE                                                      */}
        {/* ------------------------------------------------------------------ */}
        {works && works.length > 0 && (
          <section className="py-16 lg:py-32 bg-foreground text-background">
            <div className="container px-6">
              <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-16">
                <motion.div
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true }}
                  variants={fadeUp}
                  custom={0}
                >
                  <p className="text-sm uppercase tracking-[0.25em] text-primary font-medium mb-4">
                    {t("Portfolio")}
                  </p>
                  <h2 className="text-3xl sm:text-4xl lg:text-5xl font-display font-bold text-background">
                    {t("Selected")}{" "}
                    <span className="text-gradient">{t("Work")}</span>
                  </h2>
                </motion.div>
                <motion.div
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true }}
                  variants={fadeUp}
                  custom={1}
                >
                  <Link
                    to={localePath("/work")}
                    className="inline-flex items-center gap-2 text-sm font-medium text-primary hover:underline"
                  >
                    {t("View all projects")} <ArrowUpRight className="h-4 w-4" />
                  </Link>
                </motion.div>
              </div>

              <div className="grid sm:grid-cols-2 gap-6 lg:gap-8">
                {works.map((work, i) => {
                  const img = resolveAssetUrl(
                    work.desktop_screenshot || work.featured_image
                  );
                  return (
                    <motion.div
                      key={work.slug}
                      initial="hidden"
                      whileInView="visible"
                      viewport={{ once: true, margin: "-60px" }}
                      variants={fadeUp}
                      custom={i}
                    >
                      <Link
                        to={localePath(`/work/${work.slug}`)}
                        className="group block"
                      >
                        {img && (
                          <div className="aspect-[16/10] overflow-hidden rounded-xl bg-background/5 mb-5">
                            <img
                              src={img}
                              alt={work.title}
                              className="w-full h-full object-cover group-hover:scale-[1.03] transition-transform duration-700 ease-out"
                              loading="lazy"
                            />
                          </div>
                        )}
                        <div className="flex items-start justify-between gap-4">
                          <div>
                            <h3 className="font-sans font-semibold text-base lg:text-[20px] text-background group-hover:text-primary transition-colors duration-200">
                              {work.title}
                            </h3>
                            {work.industry && (
                              <p className="text-sm text-background/50 mt-1">
                                {work.industry}
                              </p>
                            )}
                          </div>
                          <ArrowUpRight className="h-5 w-5 text-background/30 group-hover:text-primary flex-shrink-0 mt-1 transition-colors duration-200" />
                        </div>
                      </Link>
                    </motion.div>
                  );
                })}
              </div>
            </div>
          </section>
        )}

        {/* ------------------------------------------------------------------ */}
        {/* FAQ                                                                */}
        {/* ------------------------------------------------------------------ */}
        <section className="py-16 lg:py-24">
          <div className="container px-6">
            <div className="max-w-2xl mx-auto text-center mb-12">
              <motion.span
                className="inline-flex items-center px-3 py-1 rounded-lg bg-secondary text-sm text-muted-foreground font-medium mb-4"
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                variants={fadeUp}
                custom={0}
              >
                {t("FAQ")}
              </motion.span>
              <motion.h2
                className="text-3xl sm:text-4xl lg:text-[2.5rem] font-display font-bold leading-tight"
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                variants={fadeUp}
                custom={1}
              >
                {t("Frequently asked questions")}
              </motion.h2>
            </div>

            <div className="max-w-[700px] mx-auto">
              <Accordion type="single" collapsible>
                {faqs.map((faq, i) => (
                  <AccordionItem key={i} value={`faq-${i}`}>
                    <AccordionTrigger className="text-left text-base">
                      {t(faq.q)}
                    </AccordionTrigger>
                    <AccordionContent className="text-muted-foreground text-base leading-relaxed">
                      {t(faq.a)}
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </div>
          </div>
        </section>

        {/* ------------------------------------------------------------------ */}
        {/* CTA                                                                */}
        {/* ------------------------------------------------------------------ */}
        <section className="py-16 lg:py-24">
          <div className="container px-6">
            <div className="max-w-2xl mx-auto text-center mb-12">
              <motion.span
                className="inline-flex items-center px-3 py-1 rounded-lg bg-secondary text-sm text-muted-foreground font-medium mb-4"
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                variants={fadeUp}
                custom={0}
              >
                {t("Get Started")}
              </motion.span>
              <motion.h2
                className="text-3xl sm:text-4xl lg:text-[2.5rem] font-display font-bold leading-tight"
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                variants={fadeUp}
                custom={1}
              >
                {t("Ready to build your next website?")}
              </motion.h2>
            </div>

            <div className="grid md:grid-cols-2 gap-6 max-w-4xl mx-auto">
              <motion.div
                className="bg-secondary/50 rounded-2xl p-10 lg:p-12 flex flex-col gap-8"
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                variants={fadeUp}
                custom={0}
              >
                <Users className="h-8 w-8 text-foreground" strokeWidth={1.5} />
                <div>
                  <h3 className="font-display text-2xl font-bold mb-2">
                    {t("Start a project")}
                  </h3>
                  <p className="text-base text-muted-foreground leading-relaxed">
                    {t(
                      "Tell us about your website goals and get a tailored proposal within 24 hours."
                    )}
                  </p>
                </div>
                <Button variant="hero" size="lg" className="w-fit" asChild>
                  <Link to={localePath("/start-a-project")}>
                    {t("Get a Free Quote")}
                    <ArrowUpRight className="h-4 w-4 opacity-60" />
                  </Link>
                </Button>
              </motion.div>

              <motion.div
                className="bg-primary rounded-2xl p-10 lg:p-12 flex flex-col gap-8"
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                variants={fadeUp}
                custom={1}
              >
                <CalendarDays
                  className="h-8 w-8 text-white"
                  strokeWidth={1.5}
                />
                <div>
                  <h3 className="font-display text-2xl font-bold mb-2 text-white">
                    {t("Book a free consultation")}
                  </h3>
                  <p className="text-base text-white/70 leading-relaxed">
                    {t(
                      "Chat with a web expert to see how we can bring your vision to life."
                    )}
                  </p>
                </div>
                <Button
                  size="lg"
                  className="w-fit border-2 border-white/30 bg-transparent text-white hover:bg-white/10"
                  asChild
                >
                  <Link to={localePath("/contact")}>
                    {t("Book a Call")}
                    <ArrowUpRight className="h-4 w-4 opacity-60" />
                  </Link>
                </Button>
              </motion.div>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default WebPage;
